import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { 
    Search, Store, LayoutDashboard, Zap, 
    ArrowRight, MapPin, ShieldCheck, ShoppingBag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const POSStoreBrowser = () => {
    const navigate = useNavigate();
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        fetchPOSSellers();
    }, []);

    const fetchPOSSellers = async () => {
        try {
            // Fetch all approved sellers from our new public endpoint
            const response = await axios.get(`${API}/sellers/`);
            setSellers(response.data);
        } catch (error) {
            console.error("Error fetching POS sellers:", error);
            // Fallback: try without trailing slash if the above fails
            try {
                const retryResponse = await axios.get(`${API}/sellers`);
                setSellers(retryResponse.data);
            } catch (retryError) {
                console.error("Retry failed:", retryError);
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredSellers = sellers.filter(s => 
        (s.store_name?.toLowerCase() || s.fullname?.toLowerCase()).includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            
            {/* Dark Hero Section */}
            <section className="bg-[#232F3E] text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-10">
                        <div className="text-center md:text-left">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[#FF9900] text-[10px] font-black uppercase tracking-widest mb-4">
                                <Zap className="w-3 h-3" /> VANN ONLINE RAPID
                            </div>
                            <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter uppercase leading-none mb-4">
                                BVG LINK <span className="text-[#FF9900]">POS ONLINE</span>
                            </h1>
                            <p className="text-gray-400 font-bold uppercase tracking-widest text-[11px] max-w-lg">
                                Chwazi yon magazen piba a pou w wè pwodwi yo aksepte nan kès POS yo kounye a.
                            </p>
                        </div>
                        <div className="flex-1 max-w-md w-full relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input 
                                type="text"
                                placeholder="Chèche yon magazen (Egz: Sophie Store...)"
                                className="w-full h-16 bg-white/5 border border-white/10 rounded-[25px] pl-14 text-white font-bold text-sm focus:outline-none focus:border-[#FF9900] transition-all"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex-1 w-full">
                <div className="flex items-center gap-3 mb-10 pb-4 border-b">
                    <LayoutDashboard className="w-6 h-6 text-[#FF9900]" />
                    <h2 className="text-2xl font-black tracking-tighter italic uppercase text-gray-900">Magazen <span className="text-[#FF9900]">POS</span></h2>
                </div>

                {loading ? (
                    <div className="flex justify-center py-24"><div className="loading-spinner"></div></div>
                ) : filteredSellers.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {filteredSellers.map(seller => (
                            <div 
                                key={seller.id}
                                onClick={() => navigate(`/pos/${seller.id}`)}
                                className="group relative bg-white rounded-3xl p-6 lg:p-10 border border-transparent shadow-xl hover:shadow-2xl hover:border-[#FF9900]/20 transition-all cursor-pointer flex flex-col h-full"
                            >
                                <div className="flex flex-row md:flex-col items-center gap-4 md:gap-6 mb-4 md:mb-8 text-left md:text-center">
                                    <div className="w-16 h-16 md:w-24 md:h-24 bg-[#FF9900] rounded-2xl md:rounded-[32px] flex items-center justify-center text-black shadow-lg shrink-0 transform md:group-hover:rotate-6 transition-transform">
                                        <Store className="w-8 h-8 md:w-12 md:h-12" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg md:text-2xl font-black text-gray-900 leading-tight italic uppercase tracking-tighter">
                                            {seller.store_name || seller.fullname}
                                        </h3>
                                        <div className="flex items-center md:justify-center gap-1.5 text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">
                                            <ShieldCheck className="w-3 h-3 text-green-500" /> Machann Verifié
                                        </div>
                                    </div>
                                </div>
                                <div className="hidden md:flex items-center justify-center gap-3 text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-8">
                                    <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Ayiti</div>
                                    <div className="flex items-center gap-1.5"><ShoppingBag className="w-3 h-3" /> POS Aktif</div>
                                </div>
                                <Button className="w-full h-12 md:h-14 bg-gray-50 group-hover:bg-[#FF9900] text-gray-400 group-hover:text-black rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all duration-500 mt-auto">
                                    Antre nan POS <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-24 text-center">
                        <Store className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-400 font-bold uppercase tracking-widest">Pa gen okenn magazen ki matche.</p>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};
